import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { PERMISSIONS } from "@/constants/permissions"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import useUser from "@/hooks/useUser"
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query"
import { useState } from "react"
import { catalog } from "./api"
import {
  pilotRequests,
  type EvidenceMatch,
  type PilotFramework,
  type PilotRequest,
} from "./pilotRequestsApi"

export function RequestMatchingPanel({ request }: { request: PilotRequest }) {
  const { user, hasPermission } = useUser()
  const { translate: t } = useTranslation()
  const client = useQueryClient()
  const [document, setDocument] = useState("")
  const [part, setPart] = useState("")
  const [framework, setFramework] = useState("")
  const [quote, setQuote] = useState("")
  const [confirmed, setConfirmed] = useState(false)
  const [companyScope, setCompanyScope] = useState(false)
  const [exclusions, setExclusions] = useState(false)
  const [autoProgress, setAutoProgress] = useState({
    scanned: 0,
    uploaded: 0,
    reused: 0,
    notAutoMatched: 0,
    matchedPairs: 0,
    failed: [] as string[],
  })
  const docs = useInfiniteQuery({
    queryKey: [
      "company-index",
      user?.organization,
      "match-documents",
      request.companyId,
    ],
    queryFn: ({ pageParam }) => catalog.documents(request.companyId, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page) => page.nextCursor ?? undefined,
  })
  const selected = docs.data?.pages
    .flatMap((p) => p.items)
    .find((d) => d.id === document)
  const requirement =
    part === "" ? undefined : request.scope.requirements[Number(part)]
  const valid =
    !!selected?.sourceUrl &&
    !!requirement &&
    requirement.frameworks.includes(framework as PilotFramework) &&
    !!quote.trim() &&
    confirmed &&
    (!companyScope || exclusions)
  const save = useMutation({
    mutationFn: (matches: EvidenceMatch[]) =>
      pilotRequests.saveMatches(request.id, request.revision, matches),
    onSuccess: () => {
      void client.invalidateQueries({
        queryKey: [
          "company-index",
          user?.organization,
          "research-requests",
          request.companyId,
        ],
      })
      setConfirmed(false)
      setQuote("")
    },
  })
  const auto = useMutation({
    mutationFn: async () => {
      let revision = request.revision
      let cursor: string | null = null
      const seen = new Set<string>()
      const totals = {
        scanned: 0,
        uploaded: 0,
        reused: 0,
        notAutoMatched: 0,
        matchedPairs: 0,
        failed: [] as string[],
      }
      setAutoProgress(totals)
      for (let page = 0; page < 1000; page++) {
        const result = await pilotRequests.autoImport(request.id, {
          expectedRevision: revision,
          cursor,
        })
        revision = result.revision
        totals.scanned += result.scanned
        totals.uploaded += result.uploaded
        totals.reused += result.reused
        totals.notAutoMatched += result.notAutoMatched
        totals.matchedPairs += result.matchedPairs
        totals.failed.push(...result.failed)
        setAutoProgress({ ...totals })
        if (!result.nextCursor) return totals
        if (seen.has(result.nextCursor))
          throw new Error("Automatic import cursor repeated")
        seen.add(result.nextCursor)
        cursor = result.nextCursor
      }
      throw new Error("Automatic import exceeded 1000 batches")
    },
    onSettled: () => {
      void client.invalidateQueries({
        queryKey: [
          "company-index",
          user?.organization,
          "research-requests",
          request.companyId,
        ],
      })
      void client.invalidateQueries({
        queryKey: [
          "company-index",
          user?.organization,
          "request-declarations",
          request.id,
        ],
      })
    },
  })
  const [batch, setBatch] = useState(0)
  const batches = useQuery({
    queryKey: [
      "company-index",
      user?.organization,
      "internal-batches",
      request.id,
      request.revision,
    ],
    queryFn: () => pilotRequests.internalBatches(request.id),
    enabled:
      hasPermission(PERMISSIONS.MC_DECLARATIONS_UPLOAD) &&
      request.matches.some((m) => m.decision === "supported"),
  })
  const selectedBatch = batches.data?.batches.find((b) => b.index === batch)
  const download = useMutation({
    mutationFn: () =>
      pilotRequests.downloadInternal(request.id, request.revision, batch),
  })
  const canEdit = hasPermission(PERMISSIONS.MC_DECLARATIONS_UPLOAD)
  const selectClass =
    "mt-1 w-full rounded-md border border-input bg-background p-2 text-sm"
  return (
    <div className="space-y-3 mt-4 border-t pt-4">
      <h3 className="font-medium">{t("CI_MATCH_TITLE")}</h3>
      <p className="text-sm text-muted-foreground">{t("CI_MATCH_HINT")}</p>
      {hasPermission(PERMISSIONS.MC_DECLARATIONS_UPLOAD) && (
        <div className="space-y-2 rounded-lg border bg-card p-3">
          <Button
            disabled={auto.isPending || save.isPending}
            onClick={() => auto.mutate()}
          >
            {t("CI_AUTO_IMPORT")}
          </Button>
          <p className="text-sm text-muted-foreground">{t("CI_AUTO_HINT")}</p>
          {(auto.isPending || auto.isSuccess) && (
            <p role="status" className="text-sm">
              {autoProgress.scanned} {t("CI_AUTO_SCANNED")} ·{" "}
              {autoProgress.uploaded} {t("CI_AUTO_UPLOADED")} ·{" "}
              {autoProgress.reused} {t("CI_AUTO_REUSED")} ·{" "}
              {autoProgress.notAutoMatched} {t("CI_AUTO_LEFT_OUT")}
            </p>
          )}
          {autoProgress.failed.length > 0 && (
            <p role="alert">
              {t("CI_AUTO_FAILED")}: {autoProgress.failed.join(", ")}
            </p>
          )}
          {auto.isError && <p role="alert">{t("CI_AUTO_ERROR")}</p>}
        </div>
      )}
      {request.matches.map((m, i) => (
        <div
          className="flex items-center justify-between gap-3 text-sm"
          key={`${m.candidateId}-${i}`}
        >
          <span>
            {m.partNumber} · {m.framework.toUpperCase()} · {m.decision}
            {m.matchMethod === "automatic_exact_pdf" &&
              ` · ${t("CI_AUTO_MATCH_LABEL")}`}
          </span>
          {canEdit && (
            <Button
              variant="outline"
              disabled={save.isPending || auto.isPending}
              onClick={() =>
                save.mutate(request.matches.filter((_, index) => index !== i))
              }
            >
              {t("CI_MATCH_REMOVE")}
            </Button>
          )}
        </div>
      ))}
      {canEdit && (
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault()
            if (
              !valid ||
              !selected ||
              !requirement ||
              save.isPending ||
              auto.isPending
            )
              return
            const match: EvidenceMatch = {
              candidateId: selected.id,
              sha256: selected.sha256,
              issuerDomain: request.scope.issuerDomain,
              partNumber: requirement.partNumber,
              framework: framework as PilotFramework,
              decision: "supported",
              sourceUrl: selected.sourceUrl!,
              quote: quote.trim(),
              scopeBasis: companyScope ? "company_scope" : "exact_part",
              scopeReviewed: true,
              exclusionsReviewed: exclusions,
            }
            save.mutate([
              ...request.matches.filter(
                (m) =>
                  !(
                    m.candidateId === match.candidateId &&
                    m.partNumber === match.partNumber &&
                    m.framework === match.framework
                  ),
              ),
              match,
            ])
          }}
        >
          <label className="block">
            {t("CI_MATCH_DOCUMENT")}
            <select
              className={selectClass}
              value={document}
              onChange={(e) => {
                setDocument(e.target.value)
                setConfirmed(false)
              }}
            >
              <option value="">—</option>
              {docs.data?.pages
                .flatMap((p) => p.items)
                .map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.filename}
                  </option>
                ))}
            </select>
          </label>
          {docs.hasNextPage && (
            <Button
              type="button"
              variant="outline"
              disabled={docs.isFetchingNextPage}
              onClick={() => void docs.fetchNextPage()}
            >
              {t("CI_MORE")}
            </Button>
          )}
          {docs.isError && (
            <p role="alert">
              {t("CI_ERROR")}{" "}
              <Button type="button" onClick={() => void docs.refetch()}>
                {t("CI_RETRY")}
              </Button>
            </p>
          )}
          <label className="block">
            {t("CI_MATCH_PART")}
            <select
              className={selectClass}
              value={part}
              onChange={(e) => {
                setPart(e.target.value)
                setFramework("")
                setConfirmed(false)
              }}
            >
              <option value="">—</option>
              {request.scope.requirements.map((r, i) => (
                <option key={i} value={String(i)}>
                  {r.partNumber}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            {t("CI_MATCH_FRAMEWORK")}
            <select
              className={selectClass}
              value={framework}
              onChange={(e) => {
                setFramework(e.target.value)
                setConfirmed(false)
              }}
            >
              <option value="">—</option>
              {requirement?.frameworks.map((f) => (
                <option key={f} value={f}>
                  {f.toUpperCase()}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            {t("CI_MATCH_QUOTE")}
            <Textarea
              value={quote}
              maxLength={5000}
              onChange={(e) => {
                setQuote(e.target.value)
                setConfirmed(false)
              }}
            />
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={companyScope}
              onChange={(e) => {
                setCompanyScope(e.target.checked)
                setConfirmed(false)
                setExclusions(false)
              }}
            />
            {t("CI_MATCH_COMPANY")}
          </label>
          {companyScope && (
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={exclusions}
                onChange={(e) => setExclusions(e.target.checked)}
              />
              {t("CI_MATCH_EXCLUSIONS")}
            </label>
          )}
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
            />
            {t("CI_MATCH_CONFIRM")}
          </label>
          <Button
            type="submit"
            disabled={!valid || save.isPending || auto.isPending}
          >
            {t("CI_MATCH_SAVE")}
          </Button>
        </form>
      )}
      {canEdit && (
        <div className="space-y-2">
          {!!batches.data?.batches.length && (
            <label className="block">
              {t("CI_INTERNAL_BATCH")}
              <select
                className={selectClass}
                value={batch}
                onChange={(e) => setBatch(Number(e.target.value))}
              >
                {batches.data.batches.map((b) => (
                  <option key={b.index} value={b.index}>
                    {b.index + 1} / {batches.data.batches.length} · {b.files}{" "}
                    {t("CI_INTERNAL_FILES")}
                  </option>
                ))}
              </select>
            </label>
          )}
          {batches.isError && (
            <p role="alert">
              {t("CI_ERROR")}{" "}
              <Button onClick={() => void batches.refetch()}>
                {t("CI_RETRY")}
              </Button>
            </p>
          )}
          <Button
            variant="outline"
            disabled={
              download.isPending ||
              !selectedBatch ||
              batches.data?.revision !== request.revision
            }
            onClick={() => download.mutate()}
          >
            {t("CI_HANDOFF_INTERNAL")}
          </Button>
          <p className="text-sm text-muted-foreground">
            {t("CI_HANDOFF_INTERNAL_HINT")}
          </p>
          {download.isError && <p role="alert">{t("CI_ERROR")}</p>}
        </div>
      )}
      {save.isError && <p role="alert">{t("CI_MATCH_ERROR")}</p>}
    </div>
  )
}
